/**
 * Usage Display Component
 * User-facing usage tracking and analytics dashboard
 */

import { useState } from 'react'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from './ui/card'
import { Button } from './ui/button'
import { Badge } from './ui/badge'
import { Progress } from './ui/progress'
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs'
import { 
  BarChart3, 
  Database,
  TrendingUp,
  AlertTriangle,
  Info,
  Zap,
  Activity
} from 'lucide-react'
import { useTier, useApiLimits } from '../contexts/TierContext'
import { UpgradePrompt } from './UpgradePrompt'
import { BILLING_UPGRADE_PATH } from '../constants/urls'

export interface UsageDisplayProps {
  showUpgradeButton?: boolean
  showTimeframes?: boolean
  compact?: boolean
  className?: string
}

export function UsageDisplay({
  showUpgradeButton = true,
  showTimeframes = true,
  compact = false,
  className = ""
}: UsageDisplayProps) {
  const { tierInfo, loading, error, getCurrentTier, getNextTier } = useTier()
  const apiUsageData = useApiLimits()
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)
  const [selectedTimeframe, setSelectedTimeframe] = useState<'hourly' | 'daily' | 'monthly'>('daily')

  if (loading) {
    return (
      <Card className={className}>
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-2 bg-gray-200 rounded w-full"></div>
            <div className="h-2 bg-gray-200 rounded w-2/3"></div>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (error || !tierInfo) {
    // Show graceful degraded state instead of error message
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-slate-500" />
            Usage Analytics
          </CardTitle>
          <CardDescription>
            Usage tracking temporarily unavailable
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center gap-2 mb-3">
              <Info className="h-4 w-4 text-blue-600" />
              <p className="text-sm font-medium text-blue-700">Free Plan Limits</p>
            </div>
            <div className="space-y-2 text-sm text-blue-600">
              <div className="flex justify-between">
                <span>Daily API calls:</span>
                <span className="font-medium">100</span>
              </div>
              <div className="flex justify-between">
                <span>Monthly API calls:</span>
                <span className="font-medium">100</span>
              </div>
              <div className="flex justify-between">
                <span>Memory spaces:</span>
                <span className="font-medium">1</span>
              </div>
            </div>
            {showUpgradeButton && (
              <Button
                onClick={() => setShowUpgradeModal(true)}
                className="w-full mt-4"
                size="sm"
                variant="outline"
              >
                <TrendingUp className="h-3 w-3 mr-1" />
                Upgrade for More
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    )
  }

  // If no API usage data is available, show a simplified view
  if (!apiUsageData || apiUsageData.currentUsage === undefined) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-primary" />
            Usage Analytics
          </CardTitle>
          <CardDescription>
            Usage tracking will be available once you start using the API
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            <Database className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p>No usage data available yet</p>
            <p className="text-xs mt-1">Make your first API call to see usage statistics</p>
          </div>
        </CardContent>
      </Card>
    )
  }

  const getUsageData = () => {
    if (!apiUsageData || !tierInfo) return null
    
    // For now, we only have monthly data from useApiLimits
    // We'll use monthly data for all timeframes as a fallback
    const current = apiUsageData.currentUsage || 0
    const limit = apiUsageData.monthlyLimit || 0
    
    const timeframeData = {
      hourly: {
        current: current, // Fallback to monthly for now
        limit: limit, // Fallback to monthly for now
        label: 'This Hour'
      },
      daily: {
        current: current, // Fallback to monthly for now
        limit: limit, // Fallback to monthly for now
        label: 'Today'
      },
      monthly: {
        current: current,
        limit: limit,
        label: 'This Month'
      }
    }
    
    return timeframeData[selectedTimeframe]
  }

  const getUsagePercentage = (current: number, limit: number | null): number => {
    if (limit === null) return 0 // Unlimited
    return Math.min((current / limit) * 100, 100)
  }

  const getUsageStatus = (percentage: number) => {
    if (percentage >= 100) return { level: 'exceeded', color: 'text-red-600', bg: 'bg-red-100' }
    if (percentage >= 90) return { level: 'critical', color: 'text-orange-600', bg: 'bg-orange-100' }
    if (percentage >= 75) return { level: 'warning', color: 'text-yellow-600', bg: 'bg-yellow-100' }
    return { level: 'good', color: 'text-green-600', bg: 'bg-green-100' }
  }

  const handleUpgrade = () => {
    // upgrade_opportunities is an array, not an object with upgrade_url
    window.open(BILLING_UPGRADE_PATH, '_blank')
    setShowUpgradeModal(false)
  }

  if (compact) {
    return (
      <>
        <CompactUsageDisplay 
          usageData={getUsageData()}
          onUpgrade={() => setShowUpgradeModal(true)}
          showUpgradeButton={showUpgradeButton}
          className={className}
        />
        <UpgradePrompt
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          onUpgrade={handleUpgrade}
          context="api_rate_limit"
          currentTier={getCurrentTier()}
          nextTier={getNextTier() || 'pro'}
          usageInfo={(() => {
            const data = getUsageData()
            if (!data) return undefined
            return {
              current: data.current,
              limit: data.limit || 0,
              percentage: getUsagePercentage(data.current, data.limit),
              warningLevel: getUsageStatus(getUsagePercentage(data.current, data.limit)).level as any
            }
          })()}
        />
      </>
    )
  }

  const usageData = getUsageData()
  const percentage = usageData ? getUsagePercentage(usageData.current, usageData.limit) : 0
  const status = getUsageStatus(percentage)

  return (
    <>
      <Card className={className}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5 text-primary" />
                Usage Analytics
              </CardTitle>
              <CardDescription>
                Monitor your API usage and track your consumption patterns
              </CardDescription>
            </div>
            {showUpgradeButton && getNextTier() && status.level !== 'good' && (
              <Button
                onClick={() => setShowUpgradeModal(true)}
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
              >
                <Zap className="h-3 w-3" />
                Upgrade
              </Button>
            )}
          </div>
        </CardHeader>
        
        <CardContent>
          <Tabs value={selectedTimeframe} onValueChange={(value) => setSelectedTimeframe(value as any)}>
            {showTimeframes && (
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="hourly">Hourly</TabsTrigger>
                <TabsTrigger value="daily">Daily</TabsTrigger>
                <TabsTrigger value="monthly">Monthly</TabsTrigger>
              </TabsList>
            )}
            
            <TabsContent value={selectedTimeframe} className="space-y-4 mt-4">
              <UsageTab
                usageData={usageData}
                percentage={percentage}
                status={status}
                getNextTier={getNextTier}
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <UpgradePrompt
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        onUpgrade={handleUpgrade}
        context="api_rate_limit"
        currentTier={getCurrentTier()}
        nextTier={getNextTier() || 'pro'}
        usageInfo={usageData ? {
          current: usageData.current,
          limit: usageData.limit || 0,
          percentage: percentage,
          warningLevel: status.level as any
        } : undefined}
      />
    </>
  )
}

function CompactUsageDisplay({
  usageData,
  onUpgrade,
  showUpgradeButton,
  className
}: {
  usageData: any
  onUpgrade: () => void
  showUpgradeButton: boolean
  className: string
}) {
  if (!usageData) {
    return (
      <Card className={className}>
        <CardContent className="p-4">
          <div className="text-center text-sm text-muted-foreground">
            No usage data available
          </div>
        </CardContent>
      </Card>
    )
  }

  const percentage = usageData.limit ? (usageData.current / usageData.limit) * 100 : 0
  const status = percentage >= 90 ? 'critical' : percentage >= 75 ? 'warning' : 'good'

  return (
    <Card className={className}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium">API Usage</span>
          <Badge variant={status === 'critical' ? 'destructive' : status === 'warning' ? 'secondary' : 'outline'}>
            {usageData.limit ? `${usageData.current}/${usageData.limit}` : `${usageData.current}/∞`}
          </Badge>
        </div>
        
        {usageData.limit && (
          <Progress value={Math.min(percentage, 100)} className="h-2 mb-2" />
        )}
        
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">
            {usageData.label} • {usageData.limit ? `${percentage.toFixed(1)}% used` : 'Unlimited'}
          </span>
          {showUpgradeButton && status !== 'good' && (
            <Button onClick={onUpgrade} variant="ghost" size="sm">
              <Zap className="h-3 w-3" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

function UsageTab({
  usageData,
  percentage,
  status,
  getNextTier
}: {
  usageData: any
  percentage: number
  status: any
  getNextTier: () => string | null
}) {
  if (!usageData) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <Database className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p>No usage data available for this timeframe</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Current Usage */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-medium flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            {usageData.label}
          </h4>
          <Badge 
            variant={status.level === 'exceeded' ? 'destructive' : 'secondary'}
            className={status.bg}
          >
            {usageData.limit ? `${usageData.current} / ${usageData.limit}` : `${usageData.current} / ∞`}
          </Badge>
        </div>
        
        {usageData.limit && (
          <>
            <Progress value={Math.min(percentage, 100)} className="h-3" />
            <div className="flex justify-between items-center mt-2">
              <span className="text-sm text-muted-foreground">
                {percentage.toFixed(1)}% used
              </span>
              <span className={`text-sm font-medium ${status.color}`}>
                {usageData.limit - usageData.current > 0 
                  ? `${usageData.limit - usageData.current} remaining`
                  : 'Limit exceeded'
                }
              </span>
            </div>
          </>
        )}
      </div>

      {/* Usage Insights */}
      <div className="grid grid-cols-3 gap-4">
        <div className="text-center">
          <div className="text-2xl font-bold text-primary">{usageData.current}</div>
          <div className="text-xs text-muted-foreground">Requests Used</div>
        </div>
        <div className="text-center">
          <div className={`text-2xl font-bold ${usageData.limit && (usageData.limit - usageData.current) < 0 ? 'text-red-600' : 'text-green-600'}`}>
            {usageData.limit ? (usageData.limit - usageData.current) : '∞'}
          </div>
          <div className="text-xs text-muted-foreground">Remaining</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-blue-600">
            {usageData.limit || '∞'}
          </div>
          <div className="text-xs text-muted-foreground">Total Limit</div>
        </div>
      </div>

      {/* Usage Status Message */}
      {status.level !== 'good' && (
        <div className={`p-3 rounded-lg border ${status.bg}`}>
          <div className="flex items-start gap-2">
            {status.level === 'exceeded' ? (
              <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5" />
            ) : (
              <Info className="h-4 w-4 text-orange-600 mt-0.5" />
            )}
            <div>
              <p className="text-sm font-medium">
                {status.level === 'exceeded' 
                  ? 'Usage Limit Exceeded'
                  : status.level === 'critical'
                  ? 'Approaching Usage Limit'
                  : 'High Usage Warning'
                }
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {status.level === 'exceeded'
                  ? 'You have exceeded your usage limit. Consider upgrading for higher limits.'
                  : `You are using ${percentage.toFixed(1)}% of your allocated resources. Consider upgrading to avoid hitting limits.`
                }
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Tier Benefits */}
      {getNextTier() && (
        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-blue-600" />
            Upgrade to {getNextTier()}
          </h4>
          <div className="text-xs text-muted-foreground">
            Get higher API limits, unlimited memories, and premium features
          </div>
        </div>
      )}
    </div>
  )
} 